import {
    TWarboxConversationActionTypes,
    TWarboxConversationInitialState,
} from './types';
import { updateWarboxConversationState } from './utils';
import {
    DEFAULT_WARBOX_CONVERSATION_STATE,
    GET_WARBOX_CONVERSATION,
    GET_WARBOX_CONVERSATIONS,
    UPSERT_WARBOX_CONVERSATION,
    UPDATE_WARBOX_CONVERSATION,
    DELETE_WARBOX_CONVERSATION,
} from './constants';
import { CLEAR_STATE, SET_ERROR, SET_LOADING } from '../types';

const POPULATED_FIELDS_TO_PRESERVE = [
    'store',
    'lead',
    'area',
    'agent',
    'assignedTo',
    'source',
    'vehicle',
];

const isObject = (value: any) =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const getDocumentId = (value: any): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string' || typeof value === 'number') {
        return String(value);
    }

    if (!isObject(value)) return undefined;

    return value._id || value.id ? String(value._id || value.id) : undefined;
};

const shouldPreservePopulatedField = (
    currentValue: any,
    incomingValue: any
) => {
    if (!isObject(currentValue)) return false;

    const currentId = getDocumentId(currentValue);
    const incomingId = getDocumentId(incomingValue);

    return Boolean(currentId && incomingId && currentId === incomingId);
};

const mergePopulatedField = (currentValue: any, incomingValue: any) => {
    if (isObject(currentValue) && isObject(incomingValue)) {
        return { ...currentValue, ...incomingValue };
    }

    if (shouldPreservePopulatedField(currentValue, incomingValue)) {
        return currentValue;
    }

    return incomingValue;
};

const mergeConversation = (currentConversation: any, incoming: any) => {
    const merged = { ...currentConversation, ...incoming };

    POPULATED_FIELDS_TO_PRESERVE.forEach((field) => {
        if (incoming[field] === undefined) return;

        merged[field] = mergePopulatedField(
            currentConversation?.[field],
            incoming[field]
        );
    });

    if (
        isObject(currentConversation?.lastMessage) &&
        isObject(incoming?.lastMessage)
    ) {
        merged.lastMessage = {
            ...currentConversation.lastMessage,
            ...incoming.lastMessage,
        };
    }

    return merged;
};

const upsertConversation = (conversations: any[] = [], incoming: any) => {
    const incomingId = incoming?._id;
    if (!incomingId) return conversations;

    const exists = conversations.some(
        (conversation) => conversation?._id === incomingId
    );

    if (!exists) return [incoming, ...conversations];

    return conversations.map((conversation) =>
        conversation?._id === incomingId
            ? mergeConversation(conversation, incoming)
            : conversation
    );
};

export default (
    state: TWarboxConversationInitialState,
    action: TWarboxConversationActionTypes
): TWarboxConversationInitialState => {
    switch (action.type) {
        case CLEAR_STATE:
            return updateWarboxConversationState(
                state,
                DEFAULT_WARBOX_CONVERSATION_STATE
            );
        case GET_WARBOX_CONVERSATION:
            return updateWarboxConversationState(state, {
                conversation: action.payload,
                loading: false,
                error: null,
            });
        case GET_WARBOX_CONVERSATIONS:
            return updateWarboxConversationState(state, {
                conversations: action.payload.conversations,
                total: action.payload.total,
                loading: false,
                error: null,
            });
        case UPSERT_WARBOX_CONVERSATION:
            return updateWarboxConversationState(state, {
                conversations: upsertConversation(
                    state.conversations || [],
                    action.payload
                ),
                conversation:
                    state.conversation?._id === action.payload._id
                        ? mergeConversation(state.conversation, action.payload)
                        : state.conversation,
                loading: false,
                error: null,
            });
        case SET_ERROR:
            return {
                ...state,
                error: action.payload,
                loading: false,
            };
        case SET_LOADING:
            return updateWarboxConversationState(state, {
                loading: true,
            });
        case UPDATE_WARBOX_CONVERSATION: {
            const updatedConversation = action.payload;
            return updateWarboxConversationState(state, {
                conversation:
                    state.conversation?._id === updatedConversation._id
                        ? updatedConversation
                        : state.conversation,
                conversations:
                    state.conversations?.map((conversation) =>
                        conversation._id === updatedConversation._id
                            ? { ...conversation, ...updatedConversation }
                            : conversation
                    ) ?? null,
                loading: false,
                error: null,
            });
        }
        case DELETE_WARBOX_CONVERSATION: {
            const conversationId = String(action.payload.conversationId || '');
            return updateWarboxConversationState(state, {
                conversation:
                    String(state.conversation?._id || '') === conversationId
                        ? null
                        : state.conversation,
                conversations:
                    state.conversations?.filter(
                        (conversation) =>
                            String(conversation?._id || '') !== conversationId
                    ) ?? null,
                total: Math.max((state.total || 0) - 1, 0),
                loading: false,
                error: null,
            });
        }
        default:
            return state;
    }
};
